import React, { useState } from 'react';
import {
  MessageSquare,
  CheckCircle,
  Clock,
  Calendar,
  Trash2,
  Eye,
  Filter,
  Search,
  X,
  Loader2,
  ListChecks,
  XCircle,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminSystemMessages } from '../../hooks/useAdminSystemMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminSystemMessages = () => {
  const toast = useToastContext();
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [detailModalMessage, setDetailModalMessage] = useState(null);
  const {
    messages,
    pagination,
    statistics,
    isLoading,
    error,
    filters,
    typeOptions,
    readStatusOptions,
    handleFilterChange,
    deleteMessage,
    isDeleting,
  } = useAdminSystemMessages();

  const handleRowSelect = (selectedRows) => {
    setSelectedMessages(selectedRows);
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedMessages.length} message(s)?`)) {
      try {
        for (const message of selectedMessages) {
          await deleteMessage(message.id);
        }
        setSelectedMessages([]);
        toast.success(null, `${selectedMessages.length} message(s) deleted successfully`);
      } catch (error) {
        toast.error(null, 'Failed to delete messages');
      }
    }
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      'booking_placed': 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
      'payment_received': 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
      'user_registered': 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-300',
      'vendor_subscribed': 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
    };
    return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const getTypeLabel = (type) => {
    const labels = {
      'booking_placed': 'Booking Placed',
      'payment_received': 'Payment Received',
      'user_registered': 'User Registered',
      'vendor_subscribed': 'Vendor Subscribed',
    };
    return labels[type] || type;
  };

  const columns = [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: 'w-50',
      render: (message) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(message.type)}`}>
          {getTypeLabel(message.type)}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-56',
      render: (message) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50 line-clamp-2 min-w-0" title={message.title || message.message}>
          {message.title || message.message?.substring(0, 60) || 'N/A'}
        </p>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-48',
      render: (message) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {formatDate(message.created_at, 'medium')}
          </span>
        </div>
      )
    },
    {
      key: 'is_read',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (message) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          message.is_read
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
        }`}>
          {message.is_read ? <><CheckCircle className="w-3 h-3 mr-1" />Read</> : <><Clock className="w-3 h-3 mr-1" />Unread</>}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      onClick: (message) => setDetailModalMessage(message),
      className: 'text-sky-600 hover:text-sky-800 dark:text-sky-400'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (message) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
          deleteMessage(message.id).then(() => {
            toast.success(null, 'Message deleted successfully');
            if (detailModalMessage?.id === message.id) setDetailModalMessage(null);
          }).catch(() => {
            toast.error(null, 'Failed to delete message');
          });
        }
      },
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error loading messages</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="System Messages"
        subtitle="View and manage system-generated messages."
        actions={
          selectedMessages.length > 0 ? (
            <Button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              variant="danger"
              className="!rounded-xl flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedMessages.length})
            </Button>
          ) : null
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.systemMessages.title}
        steps={ADMIN_PAGE_GUIDES.systemMessages.steps}
        icon={ListChecks}
      />

      {statistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{statistics.total || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Unread</p>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">{statistics.unread || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Today</p>
                <p className="text-2xl font-bold text-sky-600 dark:text-sky-400 mt-1 tabular-nums">{statistics.today || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-sky-500" />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">This Week</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{statistics.this_week || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Filters</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <SearchSelect
                label="Type"
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
                options={typeOptions}
                placeholder="All Types"
                searchable={false}
              />
              <SearchSelect
                label="Status"
                value={filters.is_read}
                onChange={(value) => handleFilterChange('is_read', value)}
                options={readStatusOptions}
                placeholder="All Messages"
                searchable={false}
              />
              <DatePicker
                label="Date From"
                selected={filters.date_from ? new Date(filters.date_from) : null}
                onChange={(date) => handleFilterChange('date_from', date ? date.toISOString().split('T')[0] : '')}
              />
              <DatePicker
                label="Date To"
                selected={filters.date_to ? new Date(filters.date_to) : null}
                onChange={(date) => handleFilterChange('date_to', date ? date.toISOString().split('T')[0] : '')}
              />
            </div>
            <div className="mt-3 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or message..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading system messages…</p>
            </div>
          ) : (
            <DataTable
              data={messages}
              columns={columns}
              actions={actions}
              isLoading={false}
              onRowSelect={handleRowSelect}
              selectable={true}
              pagination={pagination}
              compact={true}
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      {detailModalMessage && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetailModalMessage(null)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Message Details</h3>
              <button
                onClick={() => setDetailModalMessage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)] space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Type</p>
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(detailModalMessage.type)}`}>
                  {getTypeLabel(detailModalMessage.type)}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Title</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{detailModalMessage.title || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Message</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{detailModalMessage.message || 'N/A'}</p>
              </div>
              {detailModalMessage.data && Object.keys(detailModalMessage.data).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Details</p>
                  <div className="bg-gray-50 dark:bg-gray-950/40 rounded-xl p-3 space-y-2">
                    {Object.entries(detailModalMessage.data).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm gap-4">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{key}:</span>
                        <span className="text-gray-900 dark:text-gray-100 text-right break-all">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  <strong>Date:</strong> {formatDate(detailModalMessage.created_at, 'medium')} {formatTime(detailModalMessage.created_at)}
                </span>
                {detailModalMessage.read_at && (
                  <span>
                    <strong>Read at:</strong> {formatDate(detailModalMessage.read_at, 'medium')} {formatTime(detailModalMessage.read_at)}
                  </span>
                )}
                {detailModalMessage.triggered_by && (
                  <span>
                    <strong>Triggered by:</strong> {detailModalMessage.triggered_by_type} (ID: {detailModalMessage.triggered_by})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSystemMessages;
